import { Button, Card, Flex } from 'antd';
import { useTranslation } from 'translations/hooks';

import { usePermissions } from 'business/contextProviders/usePermissions';
import { SessionHeader } from 'business/quality-control/pages/record-segment-defect/components/left-panel/session-header';
import { useBoardContext } from 'business/quality-control/pages/record-segment-defect/context/board-context';
import { useDrawing } from 'business/quality-control/pages/record-segment-defect/hooks/use-drawing';
import { usePreserveScrollOnResize } from 'business/quality-control/pages/record-segment-defect/hooks/use-preserve-scroll-on-resize';
import { getExitViewRings } from 'business/quality-control/pages/record-segment-defect/utils/get-exit-view-rings';
import { matchesReportStateFilters } from 'business/quality-control/pages/record-segment-defect/utils/matches-report-state-filters';
import { SegmentDrawConfig } from 'technical/db';
import { getLocalStorageValue } from 'technical/local-storage';
import {
  BOARD_CONFIG_KEY,
  QUALITY_SCOPE,
} from 'technical/local-storage/constants';
import {
  boardConfigSchema,
  BoardConfigSchema,
} from 'technical/local-storage/types';
import { Board } from 'ui/quality-control/board';
import { HourLines } from 'ui/quality-control/board/hour-lines';
import { BoardDisplayMode, RingData } from 'ui/quality-control/board/types';
import {
  BoardViewMode,
  getBoardConfiguration,
  getHourLineLabels,
} from 'ui/quality-control/board/utils';

import { BoardActions } from './board-actions';
import { DefectStatus } from './board-actions/types';
import styles from './index.module.scss';

export const LeftPanel = ({
  rings,
  segmentDrawConfigs,
  selectedRing,
  setSelectedRing,
  openComplianceModal,
}: {
  rings: RingData[];
  segmentDrawConfigs: SegmentDrawConfig[];
  selectedRing: string | null;
  setSelectedRing: (ringId: string) => void;
  openComplianceModal: () => void;
}) => {
  const { t } = useTranslation();

  const {
    isDrawingEnable,
    strokeResetKey,
    drawingColor,
    defectFilter,
    defectReportStateFilter,
    repairReportStateFilter,
    boardViewMode,
    setSegmentKey,
    setDefectStroke,
    setDefectFilter,
    setDefectReportStateFilter,
    setRepairReportStateFilter,
    setBoardViewMode,
    setDefectId,
    pauseDrawing,
  } = useBoardContext();
  const { extraQualityPermissions } = usePermissions();

  const filteredRings = rings.map((ring) => ({
    ...ring,
    segments: ring.segments.map((segment) => ({
      ...segment,
      defects: segment.defects.filter((defect) => {
        const matchesDefectFilter =
          defectFilter !== DefectStatus.ToRepair || defect.toRepair;
        const matchesReportState = matchesReportStateFilters({
          defectReportStateFilter,
          repairReportStateFilter,
          defectSubmittedAt: defect.submittedAt,
          repairSubmittedAt: defect.repairSubmittedAt,
        });
        return matchesDefectFilter && matchesReportState;
      }),
    })),
  }));

  // This page works offline: segmentWidth comes from the local sync
  // (useSyncBoardConfigData), not a live API call like the other board views.
  const boardConfigStored = getLocalStorageValue<BoardConfigSchema>({
    scope: QUALITY_SCOPE,
    key: BOARD_CONFIG_KEY,
    schema: boardConfigSchema,
  });

  const boardConfiguration = getBoardConfiguration(
    segmentDrawConfigs ?? [],
    boardConfigStored?.segmentWidth ?? 0,
    rings.length,
    BoardDisplayMode.Complete,
  );

  const orderedRings =
    boardViewMode === BoardViewMode.Exit
      ? getExitViewRings(
          filteredRings,
          boardConfiguration?.segment ?? {
            width: 0,
            tilt: { value: 0, extendedValue: 0 },
          },
        )
      : filteredRings;

  const hourLineLabels = getHourLineLabels(boardViewMode);

  const selectedRingIndex = orderedRings.findIndex(
    (ring) => ring.id === selectedRing,
  );
  // Expressed as a fraction of the board's total height rather than an
  // absolute position: the board's SVG is scaled to fit the container width,
  // so its viewBox units don't map 1:1 to CSS pixels of the scroll container.
  const selectedRingCenterFraction =
    selectedRingIndex >= 0 && boardConfiguration
      ? (boardConfiguration.padding.top +
          selectedRingIndex * boardConfiguration.segment.height +
          boardConfiguration.segment.height / 2) /
        boardConfiguration.board.height
      : null;

  const { containerRef, onScroll } = usePreserveScrollOnResize<HTMLDivElement>(
    selectedRingCenterFraction,
  );

  const { svgRef, liveLineRef, strokes, handlers } = useDrawing({
    isDrawingEnable,
    strokeResetKey,
    segmentWidth: boardConfiguration?.segment.width ?? 0,
    segmentHeight: boardConfiguration?.segment.height ?? 0,
    boardViewMode,
    onDrawStart: setSegmentKey,
    onStrokeComplete: setDefectStroke,
  });

  const isSessionInProgress =
    segmentDrawConfigs.length > 0 && rings.length > 0 && !!boardConfiguration;

  const updateCurrentRing = (id: string) => {
    setDefectId(null);
    setSelectedRing(id);
  };

  return (
    <Flex flex={2} vertical gap={16} className={styles.panel}>
      {isSessionInProgress ? <SessionHeader /> : null}
      <Card className={styles.card} classNames={{ body: styles.cardBody }}>
        {isSessionInProgress ? (
          <>
            <BoardActions
              openComplianceModal={openComplianceModal}
              defectFilter={defectFilter}
              onDefectFilterChange={setDefectFilter}
              defectReportStateFilter={defectReportStateFilter}
              onDefectReportStateFilterChange={setDefectReportStateFilter}
              repairReportStateFilter={repairReportStateFilter}
              onRepairReportStateFilterChange={setRepairReportStateFilter}
              boardViewMode={boardViewMode}
              onBoardViewModeChange={setBoardViewMode}
              canManageCompliance={extraQualityPermissions.canEditCompliance}
            />
            {isDrawingEnable && (
              <Button
                onClick={pauseDrawing}
                style={{ alignSelf: 'flex-end', marginBottom: 8 }}
                type="primary"
              >
                {t('qualityControl.recordSegmentDefect.leftPanel.backToForm')}
              </Button>
            )}
            <div className={styles.hourLabelBar}>
              {hourLineLabels.map((label, i) => {
                const x =
                  boardConfiguration.margin.leftHourLines +
                  (boardConfiguration.ringSegmentsWidth / 4) * i;
                const leftPosition = (x / boardConfiguration.board.width) * 100;

                return (
                  <span
                    key={i}
                    className={styles.hourLabel}
                    style={{ left: `${leftPosition}%` }}
                  >
                    {label}
                  </span>
                );
              })}
            </div>
            <div
              className={styles.svgContainer}
              ref={containerRef}
              onScroll={onScroll}
            >
              <svg
                ref={svgRef}
                viewBox={`0 0 ${boardConfiguration.board.width} ${boardConfiguration.board.height}`}
                preserveAspectRatio="xMidYMin meet"
                className={styles.boardSvg}
                style={{ touchAction: isDrawingEnable ? 'none' : 'auto' }}
                {...handlers}
              >
                <Board
                  boardConfiguration={boardConfiguration}
                  rings={orderedRings}
                  selectedRing={selectedRing}
                  setSelectedRing={updateCurrentRing}
                  canManageCompliance={
                    extraQualityPermissions.canEditCompliance
                  }
                />
                <polyline
                  ref={liveLineRef}
                  fill="none"
                  stroke={drawingColor}
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeDasharray="4"
                />
                {strokes.map((stroke) => (
                  <polyline
                    key={stroke.id}
                    points={stroke.points.map((p) => `${p.x},${p.y}`).join(' ')}
                    fill="none"
                    stroke={drawingColor}
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                ))}

                <HourLines
                  labels={hourLineLabels}
                  totalSegmentWidth={boardConfiguration.ringSegmentsWidth}
                  marginX={boardConfiguration.margin.leftHourLines}
                  boardHeight={boardConfiguration.board.height}
                  hideLabels
                />
              </svg>
            </div>
          </>
        ) : null}
      </Card>
    </Flex>
  );
};
